import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EventResponse, TrainingPackageResponse, TrainingRegistrationResponse} from '../../../../../../../../../../@shared/models/response.module';
import {TrainingService} from '../../../../../../../../../../@shared/services/training.service';
import {ToastService} from '../../../../../../../../../../@core/services/toast.service';
import {formatDateDMYDots} from '../../../../../../../../../../@core/utils/utils.service';
import {DeleteModal} from '../../../../../../../../../../@shared/components/delete-modal/delete-modal';
import {EditPackageRegistration} from './components/edit-package-registration/edit-package-registration';
import {AddPackageRegistration} from '../add-package-registration/add-package-registration';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-view-package-registrations',
  standalone: false,
  templateUrl: './view-package-registrations.html',
  styleUrl: './view-package-registrations.css'
})
export class ViewPackageRegistrations implements OnInit {
  @Input() package!: TrainingPackageResponse;
  @Input() registrations: TrainingRegistrationResponse[] = [];
  @Input() event!: EventResponse;

  loading = false;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private trainingService: TrainingService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    console.log('Package:', this.package);
    console.log('Registrations:', this.registrations);
  }

  reloadRegistrations() {
    if (!this.event?.event_id) return;

    this.loading = true;
    this.trainingService.getRegistrations({ event_id: this.event.event_id }).subscribe({
      next: (allRegs) => {
        this.registrations = (allRegs || []).filter(r => r.package_id === this.package.package_id);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastService.error(this.translateService.instant('manageEvent.viewPackageRegistrations.reloadError'));
      }
    });
  }

  close(result: boolean = false) {
    this.activeModal.close(result);
  }

  formatPrice(priceCents: number, currency: string): string {
    return `${(priceCents / 100).toFixed(2)} ${currency}`;
  }

  openAddRegistrationModal() {
    const modalRef = this.modalService.open(AddPackageRegistration, { size: 'lg', centered: true });
    modalRef.componentInstance.package = this.package;
    modalRef.componentInstance.event = this.event;

    modalRef.result.then((result) => {
      if (result) {
        this.reloadRegistrations();
      }
    }).catch(() => {});
  }

  openEditRegistrationModal(registration: TrainingRegistrationResponse) {
    const modalRef = this.modalService.open(EditPackageRegistration, { size: 'lg', centered: true });
    modalRef.componentInstance.registration = registration;
    modalRef.componentInstance.package = this.package;

    modalRef.result.then((result) => {
      if (result) {
        this.reloadRegistrations();
      }
    }).catch(() => {});
  }

  deleteRegistration(registration: TrainingRegistrationResponse) {
    const modalRef = this.modalService.open(DeleteModal, { size: 'md', centered: true });
    modalRef.componentInstance.description = this.translateService.instant('manageEvent.viewPackageRegistrations.deleteConfirm');

    modalRef.result.then((confirmed) => {
      if (confirmed) {
        this.trainingService.deleteRegistration(registration.registration_id).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('manageEvent.viewPackageRegistrations.deleteSuccess'));
            const index = this.registrations.findIndex(r => r.registration_id === registration.registration_id);
            if (index > -1) {
              this.registrations.splice(index, 1);
            }
          },
          error: () => {
            this.toastService.error(this.translateService.instant('manageEvent.viewPackageRegistrations.deleteError'));
          }
        });
      }
    }).catch(() => {});
  }

  get paidCount(): number {
    return this.registrations.filter(r => r.payment_status === 'paid').length;
  }

  get pendingCount(): number {
    // Count pay_later registrations (confirmed but not yet paid)
    // Note: 'pending' status is now filtered out server-side (abandoned checkouts)
    return this.registrations.filter(r => r.payment_status === 'pay_later').length;
  }

  get totalRevenue(): number {
    return (this.package.price_cents / 100) * this.paidCount;
  }

  /**
   * Calculate net amount after platform + payment fees
   * Stripe: 2.4% + 2.00 NOK, Platform: 2%
   * Net = price × 0.956 - 2.00 per transaction
   */
  calculateNetAmount(grossAmount: number, transactionCount: number): number {
    if (!grossAmount || grossAmount <= 0) return 0;
    const net = grossAmount * 0.956 - (2 * transactionCount);
    return Math.max(0, Math.round(net * 100) / 100);
  }

  get netRevenue(): number {
    const grossRevenue = this.totalRevenue;
    return this.calculateNetAmount(grossRevenue, this.paidCount);
  }

  protected readonly formatDateDMYDots = formatDateDMYDots;
}
